import React, { useRef, useEffect, useState } from 'react';
import axios from 'axios';
import { 
  Bold, 
  Italic, 
  Underline, 
  List, 
  Link, 
  Image as ImageIcon, 
  X, 
  Loader,
  Type
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL ? `${process.env.REACT_APP_BACKEND_URL}/api` : '/api';

/**
 * Editor de Texto Simple con soporte para imágenes y subida de archivos
 */
export function RichTextEditor({ 
  value, 
  onChange, 
  placeholder = 'Escribe aquí...', 
  height = '200px',
  simple = false,
  id = 'editor' 
}) {
  const iframeRef = useRef(null);
  const fileInputRef = useRef(null);
  const [isUploading, setIsUploading] = useState(false);

  useEffect(() => {
    if (!iframeRef.current) return;

    const doc = iframeRef.current.contentDocument || iframeRef.current.contentWindow.document;
    doc.open();
    doc.write(`
      <!DOCTYPE html>
      <html>
      <head>
        <style>
          * { box-sizing: border-box; }
          body { 
            font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
            padding: 12px;
            margin: 0;
            font-size: 14px;
            line-height: 1.6;
            background: white;
            color: #333;
          }
          [contenteditable] { 
            outline: none;
            min-height: 100px;
          }
          [contenteditable]:empty:before {
            content: attr(data-placeholder);
            color: #94a3b8;
            cursor: text;
          }
          img {
            max-width: 100%;
            height: auto;
            margin: 8px 0;
            border-radius: 4px;
            display: block;
          }
          a { color: #2563eb; text-decoration: underline; }
        </style>
      </head>
      <body>
        <div contenteditable="true" data-placeholder="${placeholder}">${value || ''}</div>
      </body>
      </html>
    `);
    doc.close();

    const editable = doc.querySelector('[contenteditable]');
    if (editable) {
      editable.oninput = () => onChange(editable.innerHTML);
      editable.onblur = () => onChange(editable.innerHTML);
      // Mantener el scroll al final si es necesario
    }
  }, [id]);

  // Actualizar contenido si cambia externamente (pero no si nosotros lo causamos)
  useEffect(() => {
    if (iframeRef.current) {
      const doc = iframeRef.current.contentDocument || iframeRef.current.contentWindow.document;
      const editable = doc.querySelector('[contenteditable]');
      if (editable && editable.innerHTML !== value) {
        if (!value) editable.innerHTML = '';
        else editable.innerHTML = value;
      }
    }
  }, [value]);

  const execCommand = (command, value = null) => {
    if (iframeRef.current) {
      const doc = iframeRef.current.contentDocument || iframeRef.current.contentWindow.document;
      iframeRef.current.contentWindow.focus();
      doc.execCommand(command, false, value);
      const editable = doc.querySelector('[contenteditable]');
      if (editable) {
        onChange(editable.innerHTML);
      }
    }
  };

  const handleInsertImage = () => {
    // Ofrecer ambas opciones
    const choice = window.confirm('¿Quieres subir una foto desde tu equipo? (Aceptar) o pegar una URL (Cancelar)');
    
    if (choice) {
      fileInputRef.current?.click();
    } else {
      const imageUrl = prompt('Pega la URL de la imagen:');
      if (imageUrl) {
        insertImageAtCursor(imageUrl);
      }
    }
  };

  const insertImageAtCursor = (imageUrl) => {
    if (iframeRef.current) {
      const doc = iframeRef.current.contentDocument || iframeRef.current.contentWindow.document;
      const editable = doc.querySelector('[contenteditable]');
      
      if (editable) {
        iframeRef.current.contentWindow.focus();
        
        const img = doc.createElement('img');
        img.src = imageUrl;
        img.alt = 'Imagen subida';
        
        // Intentar insertar en la posición del cursor
        const selection = iframeRef.current.contentWindow.getSelection();
        if (selection.rangeCount > 0) {
          const range = selection.getRangeAt(0);
          range.deleteContents();
          range.insertNode(img);
          // Mover el cursor después de la imagen
          range.setStartAfter(img);
          range.setEndAfter(img);
          selection.removeAllRanges();
          selection.addRange(range);
        } else {
          editable.appendChild(img);
        }
        
        onChange(editable.innerHTML);
      }
    }
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      alert('Por favor selecciona un archivo de imagen');
      return;
    }

    setIsUploading(true);
    const formData = new FormData();
    formData.append('file', file);
    formData.append('category', 'blog_content');

    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(`${API}/media/upload`, formData, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });

      const fullUrl = `${process.env.REACT_APP_BACKEND_URL}${response.data.url}`;
      insertImageAtCursor(fullUrl);
    } catch (error) {
      console.error('Error al subir imagen:', error);
      alert('Error al subir la imagen. Inténtalo de nuevo.');
    } finally {
      setIsUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  return (
    <div className="border border-slate-300 rounded-lg overflow-hidden bg-white shadow-sm focus-within:ring-2 focus-within:ring-primary/20 transition-all">
      <div className="flex gap-1 p-2 bg-slate-50 border-b border-slate-200 flex-wrap items-center">
        <ToolButton onClick={() => execCommand('bold')} icon={<Bold className="w-4 h-4" />} title="Negrita" />
        <ToolButton onClick={() => execCommand('italic')} icon={<Italic className="w-4 h-4" />} title="Cursiva" />
        <ToolButton onClick={() => execCommand('underline')} icon={<Underline className="w-4 h-4" />} title="Subrayado" />
        
        <div className="w-px h-6 bg-slate-300 mx-1"></div>
        
        <ToolButton onClick={() => execCommand('insertUnorderedList')} icon={<List className="w-4 h-4" />} title="Lista" />
        <ToolButton onClick={() => {
          const url = prompt('Introduce la URL:');
          if (url) execCommand('createLink', url);
        }} icon={<Link className="w-4 h-4" />} title="Enlace" />
        
        <ToolButton 
          onClick={handleInsertImage} 
          icon={isUploading ? <Loader className="w-4 h-4 animate-spin" /> : <ImageIcon className="w-4 h-4" />} 
          title="Insertar imagen"
          disabled={isUploading}
        />
        
        <ToolButton onClick={() => execCommand('removeFormat')} icon={<X className="w-4 h-4" />} title="Limpiar formato" />
      </div>

      <input
        type="file"
        ref={fileInputRef}
        className="hidden"
        accept="image/*"
        onChange={handleFileUpload}
      />

      <iframe
        ref={iframeRef}
        style={{ 
          width: '100%', 
          height,
          border: 'none',
          padding: 0,
          margin: 0,
          display: 'block'
        }}
        title={id}
      />
    </div>
  );
}

function ToolButton({ onClick, icon, title, disabled }) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className={`p-2 bg-white border border-slate-200 rounded hover:bg-primary/10 hover:border-primary/30 transition-colors text-slate-700 disabled:opacity-50`}
      title={title}
    >
      {icon}
    </button>
  );
}

export default RichTextEditor;
